import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import invert from 'lodash-es/invert';
import omit from 'lodash-es/omit';
import { default as mapObject } from 'lodash-es/map';

import CONSTANTS from '@constants';
import dayjs from 'dayjs';
import { CompanyService } from '@app/shared/services/company.service';
import { IFBCollectionQueryParams, IFBCollectionService } from '@app/models/collection';
import { IFBJob, IFBJobResourceGetPageParams, IFBPagedResponseHttp } from 'interfaces';
import { JobResource } from '@app/shared/modules/resources/job.resource';
import { logger } from '@utils/logger.service';
import { MatomoService } from '@app/shared/services/matomo.service';
import { UserManager } from '@app/shared/services/user-manager.service';
import { UserInterestResource } from '@app/shared/modules/resources/user-interest.resource';

@Injectable()
export class JobsService implements IFBCollectionService<IFBJob> {
  limit = 6;
  multiJobSharingLimit = 100;

  readonly statusIdsByName: { [status: string]: string } = {
    archived: CONSTANTS.JOB_STATUS.ARCHIVED,
    closed: CONSTANTS.JOB_STATUS.ACTIVE_CLOSED,
    draft: CONSTANTS.JOB_STATUS.DRAFT,
    published: CONSTANTS.JOB_STATUS.ACTIVE_PUBLISHED
  };

  readonly statusNamesById = invert(this.statusIdsByName);

  private stopSignal = new Subject<void>();

  constructor(
    private companyService: CompanyService,
    private jobResource: JobResource,
    private paq: MatomoService,
    private userManager: UserManager,
    private userInterestResource: UserInterestResource
  ) {}

  fetchCount(status: string): Observable<number> {
    const params = {
      fields: 'id',
      limit: 1,
      offset: 0,
      status: this.statusIdsByName[status]
    };

    return this.jobResource.getPage(params).pipe(
      map(response => response.total),
      catchError(e => {
        logger.warn(`JobService.fetchCount(${status}) failed:`, e);
        return of(0);
      })
    );
  }

  previewSearchByQuery(status: string, query: string): Observable<IFBPagedResponseHttp<{ href: string; id: string }>> {
    const params = {
      fields: 'id',
      limit: 1,
      offset: 0,
      query,
      status: this.statusIdsByName[status]
    };

    return this.jobResource.getPage(params).pipe(
      catchError(e => {
        logger.warn(`JobService.previewSearchByQuery(${status}) failed:`, e);
        return of({ items: [], total: 0 } as any);
      })
    );
  }

  previewSearchByReferenceNumber(
    status: string,
    referenceNumber: string
  ): Observable<IFBPagedResponseHttp<{ href: string; id: string }>> {
    const params = {
      fields: 'id',
      limit: 1,
      offset: 0,
      referenceNumber,
      status: this.statusIdsByName[status]
    };

    return this.jobResource.getPage(params).pipe(
      catchError(e => {
        logger.warn(`JobService.previewSearchByReferenceNumber(${status}) failed:`, e);
        return of({ items: [], total: 0 } as any);
      })
    );
  }

  fetchJobsForMultiSharing(
    status: string,
    filters: IFBCollectionQueryParams
  ): Observable<Array<{ [id: string]: string }>> {
    const filtersWithoutQuery = omit(filters, 'query');
    // Define query params based on search type: referenceNumber or query
    const query = filters.query?.value
      ? filters.query.type === 'reference'
        ? { referenceNumber: filters.query.value }
        : { query: filters.query.value }
      : {};

    const params: IFBJobResourceGetPageParams = {
      fields: 'id',
      limit: this.multiJobSharingLimit,
      offset: 0,
      sort: 'hot,desc,creationDate,desc,id,asc',
      status: this.statusIdsByName[status],
      ...filtersWithoutQuery,
      ...query
    };

    return this.jobResource.getPage(params).pipe(
      map(response => response.items.map(item => ({ id: item.id }))),
      catchError(e => {
        logger.warn(`JobService.fetchJobsForMultiSharing(${status}) failed:`, e);
        return of([]);
      })
    );
  }

  fetchItems(
    status: string,
    filters: IFBCollectionQueryParams
  ): Observable<{ items: IFBJob[]; filteredCount: number }> {
    // Abort previous request if pending
    this.stopSignal.next();

    const filtersWithoutQuery = omit(filters, 'query');
    // Define query params based on search type: referenceNumber or query
    const query = filters.query?.value
      ? filters.query.type === 'reference'
        ? { referenceNumber: filters.query.value }
        : { query: filters.query.value }
      : {};

    const params: IFBJobResourceGetPageParams = {
      fields:
        'company,department,endDate,jobBranding,hot,id,location,reward,shareLink,status,title,creationDate,contactPerson',
      limit: this.limit,
      offset: 0,
      sort: 'hot,desc,creationDate,desc,id,asc',
      status: this.statusIdsByName[status],
      ...filtersWithoutQuery,
      ...query
    };

    const userInterests$ = filters?.recommended
      ? this.userInterestResource.getPage().pipe(
          map(userInterest => {
            const departments = userInterest.items
              .filter(interest => interest.interestType === 'DEPARTMENT')
              .map(({ value }) => value);
            const locations = userInterest.items
              .filter(interest => interest.interestType === 'LOCATION')
              .map(({ value }) => value);
            return { departments, locations };
          })
        )
      : of({ departments: [], locations: [] });

    return userInterests$.pipe(
      switchMap(({ departments, locations }) => {
        let noRecommendedJobs = false;

        // Apply filters to the list of recommended jobs
        if (filters.recommended) {
          // Show empty list if user has no interests selected
          noRecommendedJobs = !(departments.length || locations.length);

          if (departments.length) {
            // Pick department from filter or user interests
            params.department = filters.department || departments.join(',');
          }

          if (filters.department && !departments.includes(filters.department)) {
            // Show empty list if selected filter value is not preset in user interests
            noRecommendedJobs = true;
          }

          if (locations.length) {
            // Pick location from filter or user interests
            params.location = filters.location || locations.join(',');
          }

          if (filters.location && !locations.includes(filters.location)) {
            // Show empty list if selected filter value is not preset in user interests
            noRecommendedJobs = true;
          }

          delete params.recommended;
        }

        return noRecommendedJobs ? of({ items: [], total: 0 }) : this.jobResource.getPage(params);
      }),
      map(response => ({
        items: response.items.map((item: IFBJob) => ({
          ...item,
          expiring: item.endDate ? Math.abs(dayjs().diff(item.endDate)) < CONSTANTS.JOB_EXPIRING_TIME : false
        })),
        filteredCount: response.total
      })),
      tap(response => {
        if (params.status === CONSTANTS.JOB_STATUS.ACTIVE_PUBLISHED && this.userManager.isTalentScout()) {
          const searchQuery = {
            jobType: params.recommended ? 'Recommended' : params.hot ? 'Hot' : 'All',
            searchQuery: params.query,
            locationId: params.location,
            departmentId: params.department,
            recruiterId: this.userManager.data.id,
            tenantId: this.companyService.data.id
          };

          const search = mapObject(searchQuery, (value, key) => `${key}=${value || ''}`).join('&');

          this.paq.push(
            'trackSiteSearch',
            // Search keyword searched for
            search,
            // Search category
            'Jobs',
            // Number of results on the Search results page. Zero indicates a 'No Result Search Keyword'
            response.filteredCount
          );
        }
      }),
      catchError(e => {
        logger.warn(`JobService.fetchItems(${status}) failed:`, e);
        return of({
          items: [],
          filteredCount: 0
        });
      }),
      takeUntil(this.stopSignal)
    );
  }
}
